import React, { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, FileText, HelpCircle, AlignLeft, List, MessageSquare } from 'lucide-react';
import { ITEM_TYPES } from './surveyItemTypes';
import { getContentPreview } from './surveyHelpers';

/**
 * Simple card component for displaying survey items
 * @param {Object} item - The survey item to display
 * @param {boolean} isSelected - Whether the item is selected
 * @param {Function} onSelect - Function to call when the item is selected
 * @param {Function} onToggleVisibility - Function to toggle the item's visibility
 * @param {string} size - Card size: 'small' (1), 'medium' (2), or 'large' (3)
 */
const SurveyItemCard = ({ item, isSelected, onSelect, onToggleVisibility, size = 'medium' }) => {
  // Get height class based on size
  const getHeightClass = () => {
    switch(size) {
      case 'small': return 'h-8'; // Small card - 1 line
      case 'large': return 'h-32'; // Large card - up to 6 lines (reduced from max-h-40)
      case 'medium':
      default: return 'h-16'; // Medium card (default) - 2-3 lines
    }
  };
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const cardRef = useRef(null);
  
  const handleMouseEnter = () => {
    // Show tooltip immediately (no delay)
    setShowTooltip(true);
  };
  
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };
  
  const handleMouseMove = (e) => {
    // Always update mouse position for tracking
    setMousePosition({
      x: e.clientX,
      y: e.clientY
    });
  };
  
  // Handle tooltip positioning to ensure it's always on screen
  useEffect(() => {
    if (showTooltip && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      let top = mousePosition.y - 10;
      let left = mousePosition.x + 10;
      
      // Check if tooltip would go below viewport and adjust upward if needed
      if (top + tooltipRect.height > viewportHeight) {
        top = mousePosition.y - tooltipRect.height - 10;
      }
      
      // Check if tooltip would go off right edge of viewport
      if (left + tooltipRect.width > viewportWidth) {
        left = mousePosition.x - tooltipRect.width - 10;
      }
      
      // Apply calculated position
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
    }
  }, [showTooltip, mousePosition]);
  
  // Get icon based on item type
  const getTypeIcon = () => {
    switch (item.type) {
      case ITEM_TYPES.INFORMATIONAL: return <FileText size={12} />;
      case ITEM_TYPES.INTRODUCTION: return <MessageSquare size={12} />;
      case ITEM_TYPES.OPEN_ENDED: return <AlignLeft size={12} />;
      case ITEM_TYPES.RATING: return <List size={12} />;
      default: return <HelpCircle size={12} />;
    }
  };
  
  // Extract question number if present
  const questionNumberMatch = item.content?.match(/^Q\.(\d+)\s/);
  const questionNumber = questionNumberMatch ? questionNumberMatch[1] : null;
  
  // Determine background and styling
  const isDisabled = item.delete || item.noPrint;
  const backgroundColor = isDisabled ? 'bg-gray-100' : 'bg-white';
  
  // Get color for header bar if needed
  const headerColor = !questionNumber ? getColorForId(item.refId) : null;
  
  function getColorForId(id) {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500', 
      'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash << 5) - hash + id.charCodeAt(i);
      hash = hash & hash;
    }
    
    return colors[Math.abs(hash) % colors.length];
  }
  
  return (
    <div 
      className={`relative ${getHeightClass()}`} 
      onMouseEnter={handleMouseEnter} 
      onMouseLeave={handleMouseLeave} 
      onMouseMove={handleMouseMove}
      ref={cardRef}
    >
      <button
        className={`flex flex-col w-full h-full border rounded-lg transition overflow-hidden group
          ${isSelected ? 'border-blue-600 ring-1 ring-blue-300' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}
          ${backgroundColor}
          ${isDisabled ? 'opacity-70' : ''}`}
        onClick={onSelect}
      >
        {headerColor && <div className={`${headerColor} h-1 w-full`}></div>}
        
        <div className={`flex justify-between items-start px-1.5 ${size === 'small' ? 'pt-0.5 pb-0' : 'pt-1'}`}>
          <span className="font-bold text-xs text-blue-800 truncate max-w-[75%]">{item.refId}</span>
          <div className="flex flex-shrink-0">
            <span className="mr-0.5 text-gray-600">{getTypeIcon()}</span>
            <button className="p-0.5 rounded hover:bg-gray-200" onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility();
            }}>
              {!isDisabled ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
          </div>
        </div>
        
        <div className="text-left text-[10px] text-gray-600 px-1.5 pb-1 flex-grow overflow-hidden flex items-start">
          <div 
            className={`
              ${size === 'small' ? 'line-clamp-1' : size === 'large' ? 'line-clamp-6' : 'line-clamp-3'} 
              ${size === 'small' ? 'text-[9px]' : size === 'large' ? 'text-xs' : 'text-[10px]'}
              leading-tight w-full
            `}
          >
            {getContentPreview(item.content, size === 'small' ? 10 : size === 'large' ? 50 : 25)}
          </div>
        </div>
        
        {questionNumber && size !== 'small' && (
          <div 
            className="absolute -bottom-2 -left-2 w-6 h-6 flex items-center justify-center bg-blue-600 text-white rounded-full text-[11px] font-semibold shadow-md border-2 border-white transition-all duration-200 z-10 group-hover:scale-110 group-hover:shadow-lg group-hover:bg-blue-700 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onSelect();
            }}
          >
            {questionNumber}
          </div>
        )}
      </button>
      
      {/* Tooltip */}
      {showTooltip && (
        <div 
          ref={tooltipRef}
          className="fixed z-50 bg-white shadow-xl rounded-lg p-3 border border-blue-200"
          style={{
            width: '280px',
            maxHeight: '400px',
            overflow: 'auto',
            maxWidth: 'calc(100vw - 40px)'
          }}
        >
          {/* Add card header color strip if available */}
          {!questionNumber && headerColor && <div className={`${headerColor} h-1.5 w-full -mt-3 -mx-3 mb-2 rounded-t-lg`}></div>}
          
          {/* Title with ID and question number */}
          <div className="flex items-center justify-between mb-2 border-b pb-2">
            <h4 className="text-sm font-semibold text-blue-800">
              {questionNumber && (
                <span className="inline-flex items-center justify-center bg-blue-600 text-white rounded-full text-[11px] w-6 h-6 mr-1.5 border-2 border-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg hover:bg-blue-700 cursor-pointer">
                  {questionNumber}
                </span>
              )}
              {item.title || item.refId}
            </h4>
            <span className="text-xs text-gray-500 font-mono">{item.refId}</span>
          </div>
          
          {/* Item type icons */}
          <div className="flex items-center mb-2 text-xs text-gray-500">
            <span className="mr-1">{getTypeIcon()}</span>
            <span>{ITEM_TYPES[item.type] || 'Custom'}</span>
            {!isDisabled ? <Eye size={12} className="ml-auto" /> : <EyeOff size={12} className="ml-auto" />}
          </div>
          
          <p className="text-xs mb-2 max-h-48 overflow-y-auto">{item.content}</p>
          {item.options && item.options.length > 0 && (
            <div className="mt-2 border-t pt-1">
              <h5 className="text-xs font-semibold mb-1 text-gray-700">Options:</h5>
              <ul className="text-xs max-h-36 overflow-y-auto pl-2">
                {item.options.map((option, index) => (
                  <li key={index} className={`mb-1 ${!option.isVisible ? 'opacity-50' : ''}`}>
                    <span className="font-medium">{index + 1}.</span> {option.text}
                    {option.isTerminate && <span className="ml-1 text-red-500 font-semibold">(TERM)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyItemCard;